import * as React from 'react';
import styled from 'styled-components';

import { NoEntitiesExist, PaginatedList, Spinner } from 'components/common';
import { GLCheckbox } from 'common/components';
import { Button } from 'components/bootstrap';
import { DataTable, Modal } from 'security-app/components/common';
import { Row } from 'security-app/components/common/FormBaseStyles';
import { usePagination, useSetPagination, useSelectedRows, useSelectedRowsDispatch } from 'common/contexts';
import { useGetEventProcedures } from 'security-app/hooks/useEventProceduresAPI';
import type {
  EventProceduresAPIType,
  EventProcedureStepAPIType,
} from 'security-app/hooks/api/eventProceduresAPI.types';

type Props = {
  onClose: () => void;
  onAdd: (selectedProcedure: string) => void;
};

const NoEntitiesExistWrapper = styled.div`
  width: 100%;

  & > div {
    margin-top: 0;
    width: 100%;
  }
`;

function EventProcedureLibrary({ onClose, onAdd }: Props) {
  const localPagination = usePagination();
  const setLocalPagination = useSetPagination();
  const selectedProcedures = useSelectedRows();
  const selectedProceduresDispatch = useSelectedRowsDispatch();

  const { eventProcedures, loadingEventProcedures, pagination } = useGetEventProcedures(localPagination);

  const onPageChange = (newPage: number, newPageSize: number) => {
    setLocalPagination({ ...localPagination, page: newPage, perPage: newPageSize });
  };

  const isSelected = (eventProcedure: EventProceduresAPIType<EventProcedureStepAPIType[]>) =>
    !!selectedProcedures.find((sProcedure: EventProceduresAPIType<EventProcedureStepAPIType[]>) => eventProcedure.id === sProcedure.id);

  const onChange = (eventProcedure: EventProceduresAPIType<EventProcedureStepAPIType[]>) => (e: React.BaseSyntheticEvent) => {
    if (e.target.checked) {
      selectedProceduresDispatch({ type: 'upsert', payload: [eventProcedure] });
    } else {
      selectedProceduresDispatch({ type: 'remove', payload: [eventProcedure] });
    }
  };

  return (
    <Modal show onClose={onClose} maxWidth="700px" title="Add Procedure from Library" showClose={false}>
      {loadingEventProcedures ? (
        <Spinner text="Loading event procedures" />
      ) : (
        <PaginatedList
          activePage={localPagination.page}
          pageSize={localPagination.perPage}
          onChange={onPageChange}
          totalItems={pagination.total}
          useQueryParameter={false}>
          <DataTable condensed striped>
            {eventProcedures.length < 1 ? (
              <NoEntitiesExistWrapper>
                <NoEntitiesExist>There are no existing event procedures.</NoEntitiesExist>
              </NoEntitiesExistWrapper>
            ) : (
              <tbody>
                {eventProcedures.map((eventProcedure) => (
                  <DataTable.Row key={eventProcedure.id}>
                    <DataTable.Item $width="70px">
                      <GLCheckbox checked={isSelected(eventProcedure)} onChange={onChange(eventProcedure)} />
                    </DataTable.Item>
                    <DataTable.Item>{eventProcedure.title}</DataTable.Item>
                  </DataTable.Row>
                ))}
              </tbody>
            )}
          </DataTable>
        </PaginatedList>
      )}
      <Row $justify="flex-end" $width="100%">
        <Button type="reset" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          bsStyle="success"
          disabled={selectedProcedures.length < 1}
          onClick={() => onAdd(selectedProcedures[0].id)}>
          Add Procedure
        </Button>
      </Row>
    </Modal>
  );
}

export default EventProcedureLibrary;
